import React, { useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Download, Share2, Facebook, MessageCircle, User } from "lucide-react";
import { useSite } from "@/lib/site";

/**
 * Premium CAF Digital Membership Card
 * - Real scannable QR that opens https://cafvoice.org (with member ref)
 * - Optional member photo
 * - Download as PNG (html2canvas)
 * - Share: native share / Facebook / WhatsApp (for Instagram: download + post)
 */
export const DigitalMembershipCard = ({ member, showActions = false }) => {
  const site = useSite();
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const id = member?.member_id || "CAF-26-XXXXXX";
  const name = member?.full_name || "Member Name";
  const type = member?.membership_type || "Individual";
  const city = member?.city || "India";
  const stateName = member?.state || "";
  const since = member?.created_at ? new Date(member.created_at).getFullYear() : new Date().getFullYear();
  const photo = member?.photo_b64 || null;
  const qrValue = `https://cafvoice.org/?member=${encodeURIComponent(id)}`;
  const shareText = `I'm now a proud Digital Member of Citizen Awareness Foundation (CAF)! Member ID: ${id}. Join India's citizen-awareness movement:`;
  const shareUrl = "https://cafvoice.org/membership";

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, { scale: 3, useCORS: true, backgroundColor: null });
      const link = document.createElement("a");
      link.download = `CAF-Membership-${id}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Card downloaded! Post it on Instagram or anywhere you like.");
    } catch (e) {
      toast.error("Download failed. Please try again.");
    } finally { setDownloading(false); }
  };

  const nativeShare = async () => {
    try {
      if (cardRef.current && navigator.canShare) {
        const canvas = await html2canvas(cardRef.current, { scale: 2, useCORS: true, backgroundColor: null });
        const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
        const file = new File([blob], `CAF-Membership-${id}.png`, { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: "My CAF Digital Membership", text: `${shareText} ${shareUrl}` });
          return;
        }
      }
      if (navigator.share) {
        await navigator.share({ title: "My CAF Digital Membership", text: shareText, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        toast.success("Share text copied! Paste it anywhere.");
      }
    } catch (e) { /* user cancelled */ }
  };

  return (
    <div className="w-full max-w-md">
      {/* ------- The Card ------- */}
      <div
        ref={cardRef}
        data-testid="digital-membership-card"
        className="relative w-full aspect-[1.586/1] rounded-2xl overflow-hidden shadow-2xl"
        style={{ background: "linear-gradient(160deg, #111111 0%, #1a1a1a 55%, #0d3d20 100%)", fontFamily: "Figtree, sans-serif" }}
      >
        {/* Tricolour top band */}
        <div className="absolute top-0 inset-x-0 h-1.5 flex" aria-hidden="true">
          <div className="flex-1" style={{ background: "#FF9933" }} />
          <div className="flex-1" style={{ background: "#FFFFFF" }} />
          <div className="flex-1" style={{ background: "#138808" }} />
        </div>
        {/* Watermark chakra ring */}
        <div className="absolute -right-10 -bottom-12 h-56 w-56 rounded-full border-[10px] opacity-[0.08]" style={{ borderColor: "#FF9933" }} aria-hidden="true" />
        <div className="absolute -right-4 -bottom-6 h-40 w-40 rounded-full border-[6px] opacity-[0.08]" style={{ borderColor: "#138808" }} aria-hidden="true" />

        <div className="relative h-full p-4 sm:p-5 flex flex-col justify-between text-white">
          {/* Header row */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2.5">
              <img src="/logo-emblem.png" alt="CAF" className="h-10 w-10 rounded-full ring-1 ring-[#FF9933]/70 object-cover" crossOrigin="anonymous" />
              <div className="leading-tight">
                <div className="font-extrabold text-[13px] tracking-tight" style={{ fontFamily: "Space Grotesk, sans-serif" }}>{site.org_name || "Citizen Awareness Foundation"}</div>
                <div className="text-[8.5px] uppercase tracking-[0.16em] text-[#FF9933] font-semibold">Digital Membership Card</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[8px] uppercase tracking-[0.14em] text-white/60">Member since</div>
              <div className="text-sm font-bold text-[#FF9933]">{since}</div>
            </div>
          </div>

          {/* Middle: photo + details */}
          <div className="flex items-center gap-4">
            <div className="h-[74px] w-[74px] shrink-0 rounded-xl overflow-hidden ring-2 ring-white/25 bg-white/10 grid place-items-center">
              {photo ? (
                <img src={photo} alt={name} className="h-full w-full object-cover" />
              ) : (
                <User className="h-9 w-9 text-white/50" />
              )}
            </div>
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-[0.16em] text-white/55">Member name</div>
              <div className="text-lg sm:text-xl font-extrabold tracking-tight truncate" style={{ fontFamily: "Space Grotesk, sans-serif" }}>{name}</div>
              <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[10.5px]">
                <div><span className="uppercase tracking-widest text-white/50 text-[8.5px]">ID </span><span className="font-bold text-[#FF9933] tabular">{id}</span></div>
                <div><span className="uppercase tracking-widest text-white/50 text-[8.5px]">Type </span><span className="font-semibold">{type}</span></div>
                <div><span className="uppercase tracking-widest text-white/50 text-[8.5px]">City </span><span className="font-semibold">{city}{stateName ? `, ${stateName}` : ""}</span></div>
              </div>
            </div>
          </div>

          {/* Bottom row */}
          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[8.5px] uppercase tracking-[0.18em] text-white/70">Aware Today • Responsible Tomorrow • Stronger Together</div>
              <div className="mt-1 text-[8px] text-white/45">Section 8 Non-profit • 12A • 80G • CSR-1 • cafvoice.org</div>
            </div>
            <div className="shrink-0 rounded-lg bg-white p-1.5" data-testid="membership-card-qr">
              <QRCodeSVG value={qrValue} size={52} level="M" bgColor="#FFFFFF" fgColor="#111111" />
            </div>
          </div>
        </div>
      </div>

      {/* ------- Actions ------- */}
      {showActions && (
        <div className="mt-4 space-y-2">
          <div className="flex flex-wrap gap-2">
            <Button onClick={downloadCard} disabled={downloading} data-testid="membership-card-download" className="flex-1 rounded-full bg-[hsl(var(--orange))] hover:bg-[hsl(var(--orange-2))] text-black font-semibold">
              <Download className="h-4 w-4 mr-1.5" /> {downloading ? "Preparing…" : "Download Card"}
            </Button>
            <Button onClick={nativeShare} data-testid="membership-card-share" variant="outline" className="flex-1 rounded-full">
              <Share2 className="h-4 w-4 mr-1.5" /> Share
            </Button>
          </div>
          <div className="flex gap-2">
            <a data-testid="membership-share-facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-full text-sm font-semibold text-white transition-opacity hover:opacity-90" style={{ background: "#1877F2" }}>
              <Facebook className="h-4 w-4" /> Facebook
            </a>
            <a data-testid="membership-share-whatsapp" href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 rounded-full text-sm font-semibold text-white transition-opacity hover:opacity-90" style={{ background: "#25D366" }}>
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </div>
          <p className="text-[11px] text-[hsl(var(--muted-ink))] text-center">For Instagram: download the card, then post it as a photo or story. Scanning the QR opens cafvoice.org.</p>
        </div>
      )}
    </div>
  );
};
